/**
 * PLUGIN-KERNEL — mounts the app's composition and re-mounts it whenever the
 * active account changes.
 */
import { createMemo, onCleanup, type ParentComponent } from "solid-js";
import { Kernel, KernelContext } from "@fleet/kernel";
import { appComposition, type AppContribution, type HostServices } from "@/composition";
import { useI18n } from "@/providers/i18n-provider";
import { useOptionalPowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";

export const KernelProvider: ParentComponent = (props) => {
  const session = useSession();
  const { t } = useI18n();
  const ps = useOptionalPowerSyncContext();

  const host: HostServices = {
    db: () => ps?.db() ?? null,
    drizzle: () => null,
    userId: () => session.data?.user?.id,
    onHistoryError: () => {},
    translate: t,
  };

  const isTauri = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
  const target = isTauri ? "tauri" : "web";
  const kernel = new Kernel<AppContribution>();

  const activeAccountId = () => session.data?.user?.id ?? null;

  const mounted = createMemo(() => {
    const accountId = activeAccountId();
    const app = kernel.mount(appComposition(host, target), { accountId });
    onCleanup(() => app.unmount());
    return app;
  });

  return <KernelContext.Provider value={mounted}>{props.children}</KernelContext.Provider>;
};
